import ReactEChartsCore from 'echarts-for-react/lib/core';
import { echarts, COLOR_PALETTE, baseChartOptions } from '../shared/echarts-config';
import { ChartContainer } from '../shared/ChartContainer';
import { EmptyState } from '../shared/EmptyState';
import type { CategoryBreakdown, RegionBreakdown } from '../../../types/analytics.types';

type EngagementBreakdownsProps = {
  byCategory: CategoryBreakdown[];
  byRegion: RegionBreakdown[];
};

const buildCategoryOptions = (data: CategoryBreakdown[]) => ({
  ...baseChartOptions,
  xAxis: {
    type: 'category' as const,
    data: data.map((d) => d.categoryName),
    axisLabel: { fontSize: 11, color: '#6b7280', rotate: data.length > 5 ? 30 : 0 },
    axisLine: { lineStyle: { color: '#e5e7eb' } },
  },
  yAxis: {
    type: 'value' as const,
    axisLabel: { fontSize: 12, color: '#6b7280' },
    splitLine: { lineStyle: { color: '#f3f4f6' } },
  },
  series: [
    {
      type: 'bar',
      data: data.map((d) => d.count),
      itemStyle: {
        color: COLOR_PALETTE[0],
        borderRadius: [4, 4, 0, 0],
      },
      barMaxWidth: 40,
    },
  ],
});

const buildRegionOptions = (data: RegionBreakdown[]) => ({
  ...baseChartOptions,
  xAxis: {
    type: 'value' as const,
    axisLabel: { fontSize: 12, color: '#6b7280' },
    splitLine: { lineStyle: { color: '#f3f4f6' } },
  },
  yAxis: {
    type: 'category' as const,
    data: data.map((d) => d.stateName),
    axisLabel: { fontSize: 12, color: '#6b7280' },
    axisLine: { lineStyle: { color: '#e5e7eb' } },
  },
  series: [
    {
      type: 'bar',
      data: data.map((d) => d.count),
      itemStyle: {
        color: COLOR_PALETTE[1],
        borderRadius: [0, 4, 4, 0],
      },
      barMaxWidth: 28,
    },
  ],
});

export const EngagementBreakdowns = ({
  byCategory,
  byRegion,
}: EngagementBreakdownsProps) => {
  const hasCategory = byCategory.length > 0;
  const hasRegion = byRegion.length > 0;

  if (!hasCategory && !hasRegion) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <ChartContainer
        title="By Category"
        subtitle="Engagement breakdown by service category"
      >
        {hasCategory ? (
          <ReactEChartsCore
            echarts={echarts}
            option={buildCategoryOptions(byCategory)}
            style={{ height: 280 }}
            notMerge
            lazyUpdate
          />
        ) : (
          <EmptyState message="No category breakdown data available." />
        )}
      </ChartContainer>

      <ChartContainer
        title="By Region"
        subtitle="Top regions by engagement count"
      >
        {hasRegion ? (
          <ReactEChartsCore
            echarts={echarts}
            option={buildRegionOptions(byRegion)}
            style={{ height: 280 }}
            notMerge
            lazyUpdate
          />
        ) : (
          <EmptyState message="No region breakdown data available." />
        )}
      </ChartContainer>
    </div>
  );
};
